import React, { useEffect, useState } from 'react'
import {
  CompanyId,
  CreateAnnouncement$Request,
  DivisionDto,
  DivisionId,
  EditAnnouncement$Request,
  FileDto,
  FileId,
  GetAnnouncementFilters$Request,
  GetAnnouncementFilters$Response,
  GetAnnouncements$Request,
  GoalId,
  Lang,
  NotificationDto,
  NotificationId,
  NotificationRow,
  UserCompanyDto,
  UserNotificationDto,
  UserProfileDto,
} from '../../../../services/api.types'
import { Card } from '../../../../components/Card/Card'
import { Controller, useForm } from 'react-hook-form'
import { Grid, ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { currentLangString, isRtl } from '../../../../i18nInit'
import { t } from 'ttag'
import YumSmallSelect from '../../../../components/YumSmallSelect/YumSmallSelect'
import { selectUserCompany } from '../../../Dashboard/Dashboard'
import YumField from '../../../../components/YumInput/YumInput'

import { Button } from '../../../../components/Button/Button'
import YumSelect from '../../../../components/YumSelect/YumSelect'
import dayjs from '../../../../dayjsConfig'

import { ReactComponent as TitleIcon } from '../../../../assets/v3/icons/announcements/title.svg'
import { ReactComponent as MessageIcon } from '../../../../assets/v3/icons/announcements/message.svg'
import { ReactComponent as CtaTextIcon } from '../../../../assets/v3/icons/announcements/cta-text.svg'
import { ReactComponent as CtaUrlIcon } from '../../../../assets/v3/icons/announcements/cta-url.svg'
import { ReactComponent as ScheduleIcon } from '../../../../assets/v3/icons/announcements/schedule.svg'
import { ReactComponent as ChallengeIcon } from '../../../../assets/v3/icons/announcements/challenge.svg'
import { ReactComponent as TeamIcon } from '../../../../assets/v3/icons/announcements/team.svg'
import { ReactComponent as CountryIcon } from '../../../../assets/v3/icons/announcements/country.svg'
import { ReactComponent as LanguageIcon } from '../../../../assets/v3/icons/announcements/language.svg'
import { ReactComponent as EmailIcon } from '../../../../assets/v3/icons/announcements/email.svg'
import { ReactComponent as PushNotificationIcon } from '../../../../assets/v3/icons/announcements/push-notification.svg'

import { darkGray, gray, red } from '../../../../styles/material-ui-theme-v2'
import YumFormSwitch from '../../../../components/YumFormSwitch/YumFormSwitch'
import Typography from '@material-ui/core/Typography'
import { getCountryName } from '../../../../components/YumCountrySelect/YumCountrySelect'
import { store } from '../../../../index'
import { push } from 'connected-react-router'
import NotificationsWrapper from '../../../../components/Notification/NotificationsWrapper'
import YumFormSwitchAccordion from '../../../../components/YumSwitchAccordion/YumFormSwitchAccordion'
import YumUploadWrapper from '../../../../components/YumUpload/YumUploadWrapper'
import UserNotificationWrapper from '../../../Inbox/components/UserNotification/UserNotificationWrapper'
import Hidden from '@material-ui/core/Hidden'
import { getFullName } from '../../../../common/helpers/translations'
import YumPill from './components/YumPill/YumPill'
import { getTranslatedLanguageName } from '../../Announcements'
import YumFormListToggle from '../../../../components/YumFormListItemToggle/YumFormListToggle'
import YumButton from '../../../../components/YumButton/YumButton'

export interface ICreateAnnouncement {
  notificationId: NotificationId | null
  announcements: NotificationDto[]
  selectedCompanyId: CompanyId | null
  managedCompanies: UserCompanyDto[]
  companyDivisions: DivisionDto[]
  filters: GetAnnouncementFilters$Response
  userDto: UserProfileDto
  lastUpdate: number | null
}

export interface ICreateAnnouncementDispatchProps {
  setSelectedCompanyId(payload: CompanyId): void
  getAnnouncementFilters(payload: GetAnnouncementFilters$Request): void
  getAnnouncements(payload: GetAnnouncements$Request): void
  createAnnouncement(payload: CreateAnnouncement$Request): void
  editAnnouncement(payload: EditAnnouncement$Request): void
}

type CreateAnnouncementFormData = {
  title: string
  message: string
  image: boolean
  cta: boolean
  ctaUrl: string
  ctaText: string
  schedule: boolean
  scheduledTime: string
  filterRecipients: boolean
  challenge: GoalId | null
  team: DivisionId | null
  country: string | null
  lang: Lang | 'all' | null
  sendNotifications: boolean
  pushNotification: boolean
  email: boolean
  imageFileId: FileId | null
}

const convertScheduledTime = (time: number): string => {
  // Format the date as 'YYYY-MM-DDTHH:mm'
  return dayjs(time).format('YYYY-MM-DDTHH:mm')
}

const getDefaultValues = (announcement?: NotificationRow) => {
  if (!announcement) return {}
  return {
    title: announcement.title || '',
    message: announcement.message || '',
    cta: !!(announcement.ctaText && announcement.ctaUrl),
    ctaText: announcement.ctaText || '',
    ctaUrl: announcement.ctaUrl || '',
    image: !!announcement.imageFileId,
    imageFileId: announcement.imageFileId || '',
    schedule: announcement.scheduled,
    scheduledTime: convertScheduledTime(announcement.scheduledTime),
    filterRecipients: !!(announcement.goalId || announcement.divisionId || announcement.country || announcement.lang),
    challenge: announcement.goalId || undefined,
    team: announcement.divisionId || undefined,
    country: announcement.country || undefined,
    lang: announcement.lang || undefined,
    sendNotifications: !!(announcement.sendPushNotification || announcement.sendEmail),
    pushNotification: announcement.sendPushNotification,
    email: announcement.sendEmail,
  }
}

export default function CreateAnnouncement(props: ICreateAnnouncement & ICreateAnnouncementDispatchProps) {
  const {
    lastUpdate,
    notificationId,
    announcements,
    managedCompanies,
    selectedCompanyId,
    companyDivisions,
    filters,
    userDto,
    setSelectedCompanyId,
    createAnnouncement,
    editAnnouncement,
    getAnnouncementFilters,
    getAnnouncements,
  } = props

  const isEditMode = !!notificationId
  const announcement: NotificationDto | undefined = announcements
    ? announcements.find(({ id }) => id === notificationId)
    : undefined

  const [userCompany, setUserCompany] = useState(selectUserCompany(managedCompanies, selectedCompanyId))
  const [isSent, setIsSent] = useState(false)

  const [imageFileDto, setImageFileDto] = useState(null as FileDto | null)

  if (isEditMode && announcement) {
    useEffect(() => {
      getAnnouncements({ companyId: announcement.companyId })
    }, [])
    useEffect(() => {
      if (announcement) {
        const values = getDefaultValues(announcement)
        // console.log(values)
        // @ts-ignore
        reset(values)

        setUserCompany(selectUserCompany(managedCompanies, announcement.companyId))

        if (announcement.imageFileId && announcement.imageThumbUrl && announcement.imageFullUrl) {
          setImageFileDto({
            id: announcement.imageFileId,
            thumbnail: announcement.imageThumbUrl,
            full: announcement.imageFullUrl,
          })
        }

        if (announcement.sent) {
          setIsSent(true)
        }
      }
    }, [announcements])
  }

  // Legacy componentDidMount hook equivalent.
  React.useEffect(() => {
    setTimeout(() => {
      document.body.classList.add('show-back-btn')
    }, 0)
    return function cleanup() {
      document.body.classList.remove('show-back-btn')
    }
  }, [])

  if (isEditMode && !announcement) {
    return <Card>{t`Announcement not found`}</Card>
  }

  const getDefaultScheduledTime = (): string => {
    const now = dayjs()
    const minutesLeft = 60 - now.minute()

    // If less than 30 minutes are left, add two hours, otherwise add one hour
    const nextHour = now.add(minutesLeft < 30 ? 2 : 1, 'hour').startOf('hour')

    // Format the date as 'YYYY-MM-DDTHH:mm'
    return nextHour.format('YYYY-MM-DDTHH:mm')
  }

  const form = useForm<CreateAnnouncementFormData>({
    shouldUnregister: false,
    defaultValues: {
      title: '',
      message: '',
      cta: false,
      ctaText: '',
      ctaUrl: '',
      image: false,
      imageFileId: null,
      schedule: false,
      scheduledTime: getDefaultScheduledTime(),
      filterRecipients: false,
      challenge: undefined,
      team: undefined,
      country: undefined,
      lang: undefined,
      sendNotifications: true,
      pushNotification: true,
      email: false,
    },
  })
  const {
    register,
    setValue,
    setError,
    clearErrors,
    getValues,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = form

  const [submitInProgress, setSubmitInProgress] = useState(false)

  React.useEffect(() => {
    setSubmitInProgress(false)
  }, [lastUpdate])

  const image = watch('image')
  const cta = watch('cta')
  const schedule = watch('schedule')
  const filterRecipients = watch('filterRecipients')
  const sendNotifications = watch('sendNotifications')
  const pushNotification = watch('pushNotification')
  const email = watch('email')
  const scheduledTime = watch('scheduledTime')

  const challenge = watch('challenge')
  const team = watch('team')
  const country = watch('country')
  const lang = watch('lang')
  const imageFileId = watch('imageFileId')
  const title = watch('title')
  const message = watch('message')
  const ctaText = watch('ctaText')
  const ctaUrl = watch('ctaUrl')

  const updateUploadedImageDto = (fileDto: FileDto) => {
    setValue('imageFileId', fileDto?.id)
    setImageFileDto(fileDto)
  }

  React.useEffect(() => {
    setUserCompany(managedCompanies.find((company) => company.companyId === userCompany.companyId) as UserCompanyDto)
  }, [managedCompanies])

  const [debouncedScheduledTime, setDebouncedScheduledTime] = useState(
    isEditMode && announcement ? convertScheduledTime(announcement.scheduledTime) : scheduledTime,
  )
  useEffect(() => {
    // Set a timeout to update debounced value after 500ms
    let handler = undefined
    if (!isSent) {
      handler = setTimeout(() => {
        setDebouncedScheduledTime(scheduledTime)
        //console.log('setting debouncedScheduledTime')
      }, 500)
    }

    // Cleanup the timeout if `query` changes before 500ms
    return () => {
      if (handler) clearTimeout(handler)
    }
  }, [scheduledTime, isSent])

  React.useEffect(() => {
    if (!announcement?.sent) {
      const { companyId } = userCompany
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      getAnnouncementFilters({
        companyId,
        goalId: challenge === 'all' ? null : challenge,
        divisionId: team === 'all' ? null : team,
        country: country === 'all' ? null : country,
        lang: lang === 'all' ? null : lang,
        // TODO:
        scheduledTime: schedule ? formatDate(debouncedScheduledTime) : null,
        scheduledTimeZone: timeZone,
        editMode: isEditMode,
        notificationId: isEditMode && announcement?.id ? announcement.id : null,
      })
    }
  }, [announcement, userCompany?.companyId, challenge, team, country, lang, schedule, debouncedScheduledTime])

  const handleCompanyChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const { value } = event.target as { value: CompanyId }
    setUserCompany(managedCompanies.find((company) => company.companyId === value) as UserCompanyDto)
    setSelectedCompanyId(value)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return null
    try {
      const date = dayjs(dateString)
      return date.isValid() ? date.format() : null
    } catch (e) {
      return null
    }
  }

  const onSubmit = (data: CreateAnnouncementFormData) => {
    setSubmitInProgress(true)
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (isEditMode) {
      editAnnouncement({
        id: notificationId,
        title: data.title,
        message: data.message,
        ctaText: data.cta ? data.ctaText : null,
        ctaUrl: data.cta ? data.ctaUrl : null,
        imageFileId: data.image ? data.imageFileId : null,
        scheduledTime: data.schedule ? formatDate(data.scheduledTime) : null,
        goalId: data.filterRecipients ? data.challenge : null,
        divisionId: data.filterRecipients ? data.team : null,
        country: data.filterRecipients ? data.country : null,
        lang: data.filterRecipients ? data.lang : null,
        sendPushNotification: data.sendNotifications && data.pushNotification,
        sendEmail: data.sendNotifications && data.email,
        scheduledTimeZone: timeZone,
      })
    } else {
      createAnnouncement({
        companyId: userCompany.companyId,
        title: data.title,
        message: data.message,
        ctaText: data.cta ? data.ctaText : null,
        ctaUrl: data.cta ? data.ctaUrl : null,
        imageFileId: data.image ? data.imageFileId : null,
        scheduledTime: data.schedule ? formatDate(data.scheduledTime) : null,
        goalId: data.filterRecipients && data.challenge !== 'all' ? data.challenge : null,
        divisionId: data.filterRecipients && data.team !== 'all' ? data.team : null,
        country: data.filterRecipients && data.country !== 'all' ? data.country : null,
        lang: data.filterRecipients && data.lang !== 'all' ? data.lang : null,
        sendPushNotification: data.sendNotifications && data.pushNotification,
        sendEmail: data.sendNotifications && data.email,
        scheduledTimeZone: timeZone,
      })
    }
  }

  const handleCancel = () => {
    store.dispatch(push(`/admin/announcements`))
  }

  const validateFutureDate = (value: string | number | Date) => {
    // console.log('validateFutureDate', value)
    const selectedDate = new Date(value)
    const currentDate = new Date()
    return selectedDate > currentDate || t`Please select a future date and time`
  }

  const titleField = (
    <Controller
      name="title"
      rules={{
        required: true,
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Title`}
          type="text"
          error={!!errors.title}
          startIcon={TitleIcon}
          style={{ marginBottom: '16px' }}
          {...otherField}
        />
      )}
    />
  )

  const messageField = (
    <Controller
      name="message"
      rules={{
        required: true,
      }}
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        <YumField
          label={t`Message`}
          error={!!errors.message}
          startIcon={MessageIcon}
          multiline
          minRows={2}
          style={{ marginBottom: '8px' }}
          {...otherField}
        />
      )}
    />
  )

  const challengeField = filters.challenges && filters.challenges.length > 0 && (
    <Controller
      name="challenge"
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        // @ts-ignore
        <YumSelect label={t`Challenge`} startIcon={ChallengeIcon} style={{ marginBottom: 10 }} {...otherField}>
          <option value="all">{t`All challenges`}</option>
          {filters.challenges.map(({ id, title, count }: any, index) => (
            <option key={index} value={id}>
              {title} ({count})
            </option>
          ))}
        </YumSelect>
      )}
    />
  )

  const teamIds = new Set((companyDivisions || []).filter((d) => !d.categoryId).map((d) => d.id))
  const teamsOnly = filters.teams ? filters.teams.filter((team: any) => teamIds.has(team.id)) : []

  const teamField = teamsOnly.length > 0 && (
    <Controller
      name="team"
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        // @ts-ignore
        <YumSelect label={t`Team`} startIcon={TeamIcon} style={{ marginBottom: 10 }} {...otherField}>
          <option value="all">{t`All teams`}</option>
          {teamsOnly.map(({ id, title, count }: any, index) => (
            <option key={index} value={id}>
              {title} ({count})
            </option>
          ))}
        </YumSelect>
      )}
    />
  )

  const countryField = filters.countries && filters.countries.length > 0 && (
    <Controller
      name="country"
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        // @ts-ignore
        <YumSelect label={t`Country`} startIcon={CountryIcon} style={{ marginBottom: 10 }} {...otherField}>
          <option value="all">{t`All countries`}</option>
          {filters.countries.map(({ id, title, count }: any, index) => (
            <option key={index} value={title}>
              {getCountryName(title)} ({count})
            </option>
          ))}
        </YumSelect>
      )}
    />
  )

  const langField = filters.languages && filters.languages.length > 0 && (
    <Controller
      name="lang"
      control={control}
      render={({ field: { ref, ...otherField } }) => (
        // @ts-ignore
        <YumSelect label={t`Language`} startIcon={LanguageIcon} style={{ marginBottom: 10 }} {...otherField}>
          <option value="all">{t`All languages`}</option>
          {filters.languages.map(({ id, title, count }: any, index) => (
            <option key={index} value={title}>
              {getTranslatedLanguageName(title, currentLangString)} ({count})
            </option>
          ))}
        </YumSelect>
      )}
    />
  )

  const scheduledTimeField = (
    <>
      <Controller
        name="scheduledTime"
        rules={{
          required: schedule,
          validate: schedule ? validateFutureDate : undefined,
        }}
        control={control}
        render={({ field: { ref, ...otherField } }) => (
          <YumField
            type="datetime-local"
            label={t`Scheduled time`}
            startIcon={ScheduleIcon}
            error={!!errors.scheduledTime}
            shrink={true}
            {...otherField}
          />
        )}
      />
      {errors.scheduledTime && (
        <div style={{ color: red, position: 'relative', top: -8 }}>{errors.scheduledTime.message}</div>
      )}
    </>
  )

  const ctaUrlField = (
    <>
      <Controller
        name="ctaUrl"
        rules={{
          required: cta,
          //validate: validateFutureDate,
        }}
        control={control}
        render={({ field: { ref, ...otherField } }) => (
          <YumField
            type="url"
            label={t`Call to action URL`}
            startIcon={CtaUrlIcon}
            /*onChange={handleStartDateChange}*/
            placeholder="https://"
            error={!!errors.ctaUrl}
            shrink={true}
            {...otherField}
          />
        )}
      />
      {errors.ctaUrl && <div style={{ color: red, position: 'relative', top: -8 }}>{errors.ctaUrl.message}</div>}
    </>
  )

  const ctaTextField = (
    <>
      <Controller
        name="ctaText"
        rules={{
          required: cta,
          //validate: validateFutureDate,
        }}
        control={control}
        render={({ field: { ref, ...otherField } }) => (
          <YumField
            type="text"
            label={t`Call to action text`}
            startIcon={CtaTextIcon}
            /*onChange={handleStartDateChange}*/
            placeholder={t`Open link`}
            error={!!errors.ctaText}
            shrink={true}
            {...otherField}
          />
        )}
      />
      {/*{errors.ctaText && (
        <div style={{ color: red, position: 'relative', top: -8 }}>{errors.ctaText.message}</div>
      )}*/}
    </>
  )

  const imageAccordion = (
    <YumFormSwitchAccordion label={t`Image`} fieldName="image" form={form}>
      <YumUploadWrapper
        companyId={userCompany.companyId}
        updateUploadedImageDto={updateUploadedImageDto}
        existingImageDto={imageFileDto}
      />
    </YumFormSwitchAccordion>
  )

  const ctaAccordion = (
    <YumFormSwitchAccordion label={t`Call to action`} fieldName="cta" form={form}>
      {ctaTextField}
      {ctaUrlField}
    </YumFormSwitchAccordion>
  )

  const scheduleAccordion = (
    <YumFormSwitchAccordion label={t`Schedule`} fieldName="schedule" form={form}>
      {scheduledTimeField}
    </YumFormSwitchAccordion>
  )

  const filterAccordion = (
    <YumFormSwitchAccordion label={t`Filter recipients`} fieldName="filterRecipients" form={form}>
      {challengeField}
      {teamField}
      {countryField}
      {langField}
    </YumFormSwitchAccordion>
  )

  const pushNotificationField = (
    <YumFormListToggle
      label={t`Push notification`}
      name="pushNotification"
      icon={<PushNotificationIcon />}
      form={form}
    />
  )

  const emailField = <YumFormListToggle label={t`Email`} name="email" icon={<EmailIcon />} form={form} />

  const sendNotificationsAccordion = (
    <YumFormSwitchAccordion label={t`Send notifications`} fieldName="sendNotifications" form={form}>
      {pushNotificationField}
      {emailField}
    </YumFormSwitchAccordion>
  )

  const announcementLimitExceeded = filters.announcementLimitExceeded && !isSent

  const actionButtons = (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px', gap: '16px' }}>
      <YumButton variant="outlined" onClick={handleCancel} disabled={submitInProgress}>{t`Cancel`}</YumButton>
      <YumButton
        type="submit"
        showLoader={!announcementLimitExceeded}
        disabled={announcementLimitExceeded || submitInProgress}
        fullWidth={true}
      >
        {isEditMode ? t`Update` : t`Send`}
      </YumButton>
    </div>
  )

  const divider = <div style={{ backgroundColor: gray, height: '2px', margin: '8px 0' }}></div>

  const { firstName, lastName, email: userEmail } = userDto
  const fullName = getFullName(firstName, lastName, userEmail)

  const preview = (
    <>
      <div style={{ marginBottom: '24px' }}>
        <Typography variant="subtitle2" style={{ marginBottom: '18px', fontSize: '20px' }}>
          {t`Preview`}
        </Typography>
        <UserNotificationWrapper
          isOpen={true}
          notification={
            {
              companyName: userCompany.companyName,
              senderName: fullName,
              read: true,
              title: title || t`Title`,
              message: message || t`Message`,
              sentAt: scheduledTime ? dayjs(scheduledTime).valueOf() : null,
              ctaText: cta ? ctaText : null,
              ctaUrl: cta ? ctaUrl : null,
              imageFileId: image && imageFileDto ? imageFileDto.id : null,
              imageThumbUrl: image && imageFileDto ? imageFileDto.thumbnail : null,
              imageFullUrl: image && imageFileDto ? imageFileDto.full : null,
            } as UserNotificationDto
          }
        />
      </div>
      {announcement &&
        (announcement.goalId || announcement.divisionId || announcement.country || announcement.lang) && (
          <div style={{ marginBottom: '24px' }}>
            <Typography variant="subtitle2" style={{ marginBottom: '18px', fontSize: '20px' }}>
              {t`Filters`}
            </Typography>
            <div>
              {announcement.goalId && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>{t`Challenge`}: </strong>
                  {/* @ts-ignore */}
                  {props.filters.challenges.find((c: any) => c.id === announcement.goalId)?.title ||
                    t`Deleted challenge`}
                </div>
              )}
              {announcement.divisionId && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>{t`Team`}: </strong> {/* @ts-ignore */}
                  {props.filters.teams.find((c: any) => c.id === announcement.divisionId)?.title}
                </div>
              )}
              {announcement.country && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>{t`Country`}: </strong> {getCountryName(announcement.country)}
                </div>
              )}
              {announcement.lang && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>{t`Language`}: </strong>
                  {getTranslatedLanguageName(announcement.lang, currentLangString)}
                </div>
              )}
            </div>
          </div>
        )}
      <div style={{ marginBottom: '20px' }}>
        <Typography variant="subtitle2" style={{ marginBottom: 18 }}>
          {t`Recipients`}
        </Typography>
        {!isSent ? (
          <>
            <div>
              {t`Approximately`}: <YumPill users={filters.totalUserCount} />
            </div>
            {sendNotifications && pushNotification ? (
              <div style={{ marginTop: '10px' }}>
                {t`With push notifications enabled`}: <YumPill users={filters.pushEnabledUserCount} />
              </div>
            ) : (
              ''
            )}
            {sendNotifications && pushNotification ? (
              <div style={{ marginTop: '10px' }}>
                {t`With emails enabled`}: <YumPill users={filters.emailEnabledUserCount} />
              </div>
            ) : (
              ''
            )}
          </>
        ) : (
          <>
            <div style={{ marginBottom: '10px' }}>
              {t`Sent to`}: <YumPill users={announcement?.sentTo} />
            </div>
            <div>
              {t`Read by`}: <YumPill users={announcement?.readBy} />
            </div>
          </>
        )}
      </div>
    </>
  )

  const announcementForm = (
    <form onSubmit={handleSubmit(onSubmit)}>
      {titleField}
      {messageField}
      {divider}
      {imageAccordion}
      {divider}
      {ctaAccordion}
      {!isSent && (
        <>
          {divider}
          {scheduleAccordion}
          {divider}
          {filterAccordion}
          {divider}
          {sendNotificationsAccordion}
        </>
      )}
      <Hidden mdUp>
        <div style={{ marginTop: '24px' }}>{preview}</div>
      </Hidden>
      {actionButtons}
    </form>
  )

  return (
    <Card>
      <Grid container={true} spacing={10} style={{ marginBottom: '1px' }}>
        <Grid item={true} xs={12} md={12} style={{ paddingBottom: '8px' }}>
          <Grid
            container
            style={{ marginBottom: '1rem' }}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item style={{ flexShrink: '0', paddingRight: isRtl ? 0 : '10px', paddingLeft: isRtl ? '10px' : 0 }}>
              <h1 style={{ marginBottom: 3 }}>
                {isEditMode ? t`Edit announcement` : t`Create announcement`}
                {/*{!teamsLoading && <span style={{ opacity: 0.5, whiteSpace: 'nowrap' }}> ({companyTeams.length})</span>}*/}
              </h1>
              {/*<Button className="register__submit" url={ROUTES.ADMIN_USER_NEW.to} text={t`Add user`} />*/}
            </Grid>

            <Grid item style={{ overflow: 'hidden' }}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                style={{ flexWrap: 'nowrap', gap: '10px' }}
              >
                {/*<Grid
              item
              style={{
                overflow: 'hidden',
                //paddingRight: isRtl ? 0 : '10px',
                //paddingLeft: isRtl ? '10px' : 0,
                flexShrink: '0',
              }}
            >
              <CreateAnnouncementWrapper companyId={userCompany.companyId} />
            </Grid>*/}
                {managedCompanies.length > 1 ? (
                  <Grid item style={{ overflow: 'hidden' }}>
                    <YumSmallSelect
                      variant="outlined"
                      value={userCompany.companyId}
                      onChange={handleCompanyChange}
                      disabled={isEditMode}
                    >
                      {managedCompanies.map((userCompany: UserCompanyDto, keyIndex: number) => (
                        <option key={keyIndex} value={userCompany.companyId}>
                          {userCompany.companyName}
                        </option>
                      ))}
                    </YumSmallSelect>
                  </Grid>
                ) : (
                  ''
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item={true}
          xs={12}
          md={6}
          style={{
            display: 'flex',
            flexDirection: 'column',
            //maxWidth: '528px',
            marginLeft: 'auto',
            marginRight: 'auto',
            //height: 'calc(100vh - 80px)',
            paddingTop: 0,
            paddingBottom: 0,
          }}
        >
          <NotificationsWrapper />
          {announcementForm}
        </Grid>
        <Hidden smDown>
          <Grid
            item={true}
            xs={12}
            md={6}
            style={
              isRtl
                ? { borderRight: `1px solid ${darkGray}`, paddingTop: 0 }
                : { borderLeft: `1px solid ${darkGray}`, paddingTop: 0 }
            }
          >
            {preview}
          </Grid>
        </Hidden>
      </Grid>
    </Card>
  )
}
